import { useState, useEffect, useRef, useCallback } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  Tooltip, Legend, Title,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { searchTransactions, getSuggestions, getFiscalYearSettings, getTransactionFYYears } from '../../api'
import { useBusinessContext } from '../../context/BusinessContext'
import { snapshotPage, popPageSnapshot, restoreScroll } from '../../utils/pageState'
import { TransactionsTable } from '../../components/txDisplay'
import type { SearchResponse, SuggestionResult, ChartEntry, FiscalYearSettings } from '../../types'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Title)

// ── Constants ──────────────────────────────────────────────────────────────────
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

const money = (v: number) =>
  '$' + Math.abs(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// ── FY helpers ─────────────────────────────────────────────────────────────────
function getFiscalYear(dateStr: string, fy: FiscalYearSettings): number {
  if (fy.followsCalendar) return new Date(dateStr).getFullYear()
  const d = new Date(dateStr + 'T12:00:00')
  const month = d.getMonth() + 1; const day = d.getDate(); const year = d.getFullYear()
  if (month > fy.startMonth || (month === fy.startMonth && day >= fy.startDay)) return year + 1
  return year
}

function getFYDateRange(fyYear: number, fy: FiscalYearSettings): [string, string] {
  if (fy.followsCalendar) return [`${fyYear}-01-01`, `${fyYear}-12-31`]
  const pad = (n: number) => String(n).padStart(2, '0')
  const dateFrom = `${fyYear - 1}-${pad(fy.startMonth)}-${pad(fy.startDay)}`
  const endD = new Date(fyYear, fy.startMonth - 1, fy.startDay - 1)
  return [dateFrom, endD.toISOString().slice(0, 10)]
}

function fyLabel(fyYear: number, followsCalendar: boolean): string {
  return followsCalendar ? String(fyYear) : `FY ${fyYear}`
}

// Months belonging to a fiscal year, capped at the current month (no future months).
// e.g. FY2026 (Jul start) → Jul '25, Aug '25 … up to the current month.
function fyMonthPills(fyYear: number, fy: FiscalYearSettings): { year: number; month: number; label: string }[] {
  const [from, to] = getFYDateRange(fyYear, fy)
  const start = new Date(from + 'T12:00:00')
  const end   = new Date(to + 'T12:00:00')
  const now   = new Date()
  const capYM = now.getFullYear() * 12 + now.getMonth()   // current month index
  const endYM = end.getFullYear() * 12 + end.getMonth()
  const out: { year: number; month: number; label: string }[] = []
  const d = new Date(start.getFullYear(), start.getMonth(), 1)
  while (d.getFullYear() * 12 + d.getMonth() <= endYM) {
    if (d.getFullYear() * 12 + d.getMonth() > capYM) break  // skip future months
    out.push({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      label: `${MONTH_NAMES[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`,
    })
    d.setMonth(d.getMonth() + 1)
  }
  return out
}

function toDateStr(d: Date) { return d.toISOString().slice(0, 10) }

// ── Chart builder ──────────────────────────────────────────────────────────────
type ChartMode = 'month' | 'category' | 'tag'

interface ChartResult { labels: string[]; datasets: unknown[]; avgInc: number; avgSpd: number }

function buildChartData(
  mode: ChartMode,
  monthlyTotals: ChartEntry[], monthlyIncomeTotals: ChartEntry[],
  categoryTotals: ChartEntry[], tagTotals: ChartEntry[],
  totalEarned: number, totalSpent: number,
): ChartResult {
  const spendBg = 'rgba(99,102,241,0.65)'; const spendHov = 'rgba(99,102,241,0.85)'
  const incomeBg = 'rgba(16,185,129,0.55)'; const incomeHov = 'rgba(16,185,129,0.75)'

  if (mode === 'month') {
    const labels = monthlyTotals.map(m => m.label)
    const incMap = Object.fromEntries(monthlyIncomeTotals.map(m => [m.label, m.total]))
    const incData = labels.map(l => incMap[l] ?? 0)
    const mc = labels.length
    const avgInc = mc > 0 ? totalEarned / mc : 0
    const avgSpd = mc > 0 ? totalSpent  / mc : 0
    return {
      labels, avgInc, avgSpd,
      datasets: [
        { type: 'bar' as const, label: 'Expenses', order: 2,
          data: monthlyTotals.map(m => m.total),
          backgroundColor: spendBg, hoverBackgroundColor: spendHov,
          borderRadius: 6, borderSkipped: false as const, barPercentage: 0.5, categoryPercentage: 0.75 },
        { type: 'bar' as const, label: 'Revenue', order: 2,
          data: incData,
          backgroundColor: incomeBg, hoverBackgroundColor: incomeHov,
          borderRadius: 6, borderSkipped: false as const, barPercentage: 0.5, categoryPercentage: 0.75 },
        { type: 'line' as const, label: 'Avg Revenue', order: 0,
          data: labels.map(() => avgInc),
          borderColor: 'rgba(16,185,129,0.85)', backgroundColor: 'transparent',
          borderWidth: 2, borderDash: [6, 4], pointRadius: 0, tension: 0, hidden: avgInc <= 0 },
        { type: 'line' as const, label: 'Avg Expenses', order: 0,
          data: labels.map(() => avgSpd),
          borderColor: 'rgba(239,68,68,0.8)', backgroundColor: 'transparent',
          borderWidth: 2, borderDash: [6, 4], pointRadius: 0, tension: 0, hidden: avgSpd <= 0 },
      ],
    }
  }

  const entries = mode === 'category' ? categoryTotals : tagTotals
  return {
    labels: entries.map(e => e.label), avgInc: 0, avgSpd: 0,
    datasets: [
      { type: 'bar' as const, label: 'Amount', order: 2,
        data: entries.map(e => e.total),
        backgroundColor: spendBg, hoverBackgroundColor: spendHov,
        borderRadius: 6, borderSkipped: false as const, barPercentage: 0.6, categoryPercentage: 0.8 },
    ],
  }
}

// ── Search params ──────────────────────────────────────────────────────────────
interface ActiveFilter { field: string; value: string; label: string }

function buildSearchParams(
  filters: ActiveFilter[], q: string | null, dateFrom: string, dateTo: string,
): Record<string, string> {
  const params: Record<string, string> = { dateFrom, dateTo }
  const byField: Record<string, string[]> = {}
  for (const f of filters) {
    if (!byField[f.field]) byField[f.field] = []
    byField[f.field].push(f.value)
  }
  if (byField['merchant'])    params.merchants     = byField['merchant'].join(',')
  if (byField['category'])    params.categories    = byField['category'].join(',')
  if (byField['subcategory']) params.subcategories = byField['subcategory'].join(',')
  if (byField['tag'])         params.tags          = byField['tag'].join(',')
  if (q)                      params.q             = q
  return params
}

// ── Sort types ─────────────────────────────────────────────────────────────────
type SortField = 'merchant' | 'category' | 'date' | 'charges' | null
type SortDir   = 'asc' | 'desc'

// ── Page state ─────────────────────────────────────────────────────────────────
interface BizTxPageState {
  selectedFY: number | null; chartMode: ChartMode
  sortField: SortField; sortDir: SortDir
  activeFilters: ActiveFilter[]; query: string; searchPage: number
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function BizTransactions() {
  const navigate = useNavigate()
  const { selectedBusinessId: bizId } = useBusinessContext()

  const [selectedFY,  setSelectedFY]  = useState<number | null>(null)
  const [chartMode,   setChartMode]   = useState<ChartMode>('month')
  const [sortField,   setSortField]   = useState<SortField>(null)
  const [sortDir,     setSortDir]     = useState<SortDir>('asc')
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [query,       setQuery]       = useState('')
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null)
  const [searchPage,  setSearchPage]  = useState(1)
  const [suggestions, setSuggestions] = useState<SuggestionResult | null>(null)
  const [showSug,     setShowSug]     = useState(false)
  const [sugActiveIdx, setSugActiveIdx] = useState(-1)
  const [isSearching, setIsSearching] = useState(false)
  const [pendingScroll, setPendingScroll] = useState<number | null>(null)

  const fyInitialized = useRef(false)
  const searchWrapRef = useRef<HTMLDivElement>(null)
  const debounceRef   = useRef<ReturnType<typeof setTimeout>>()
  const searchSeqRef  = useRef(0)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartClickRef = useRef<(event: any, elements: any[]) => void>(() => {})

  // ── FY settings ───────────────────────────────────────────────────────────

  const { data: fySettings } = useQuery({
    queryKey: ['fiscal-year-settings'],
    queryFn:  getFiscalYearSettings,
    staleTime: 300_000,
  })

  const fy: FiscalYearSettings = fySettings ?? { followsCalendar: true, startMonth: 1, startDay: 1 }
  const today     = new Date()
  const currentFY = getFiscalYear(toDateStr(today), fy)

  const { data: fyYears = [] } = useQuery({
    queryKey: ['fy-years-biz', bizId],
    queryFn:  () => getTransactionFYYears(false, bizId),
    staleTime: 5 * 60_000,
    enabled:  bizId != null,
  })

  const ALL_DATE_FROM = '2000-01-01'
  const ALL_DATE_TO   = `${today.getFullYear() + 1}-12-31`

  // Derive date range: month filter in activeFilters > FY > ALL
  const monthFilter = activeFilters.find(f => f.field === 'month')
  const [dateFrom, dateTo] = (() => {
    if (monthFilter) {
      const [yr, mo] = monthFilter.value.split('-').map(Number)
      const lastDay  = new Date(yr, mo, 0).getDate()
      return [`${yr}-${String(mo).padStart(2,'0')}-01`, `${yr}-${String(mo).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`]
    }
    if (selectedFY !== null) return getFYDateRange(selectedFY, fy)
    return [ALL_DATE_FROM, ALL_DATE_TO]
  })()

  // ── Search core ───────────────────────────────────────────────────────────

  const doSearch = useCallback(async (
    filters: ActiveFilter[], q: string, dfrom: string, dto: string, page = 1,
  ) => {
    const seq    = ++searchSeqRef.current
    const params = buildSearchParams(filters, q.trim() || null, dfrom, dto)
    setIsSearching(true)
    try {
      const result = await searchTransactions({
        ...params,
        isPersonal: false,
        businessId: bizId ?? undefined,
      })
      if (seq !== searchSeqRef.current) return
      setSearchResults(result)
      setSearchPage(page)
    } finally {
      if (seq === searchSeqRef.current) setIsSearching(false)
    }
  }, [bizId])

  // Default to current FY + initial search on FY settings load
  useEffect(() => {
    if (!fySettings || fyInitialized.current) return
    const snap = popPageSnapshot<BizTxPageState>('biz-transactions')
    if (snap) {
      const s = snap.state
      setSelectedFY(s.selectedFY)
      setChartMode(s.chartMode)
      setSortField(s.sortField)
      setSortDir(s.sortDir)
      setActiveFilters(s.activeFilters ?? [])
      setQuery(s.query ?? '')
      setSearchPage(s.searchPage ?? 1)
      setPendingScroll(snap.scroll)
      const [df, dt] = s.selectedFY !== null ? getFYDateRange(s.selectedFY, fy) : [dateFrom, dateTo]
      doSearch(s.activeFilters ?? [], s.query ?? '', df, dt, s.searchPage ?? 1)
    } else {
      setSelectedFY(currentFY)
      const [df, dt] = getFYDateRange(currentFY, fy)
      doSearch([], '', df, dt)
    }
    fyInitialized.current = true
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fySettings])

  // Scroll restore — wait until the restored results have actually rendered,
  // otherwise the page isn't tall enough yet and the scroll position is clamped.
  useEffect(() => {
    if (pendingScroll === null || !searchResults) return
    const y = pendingScroll
    setPendingScroll(null)
    restoreScroll(y)
  }, [pendingScroll, searchResults])

  // Re-search when bizId changes — but NOT on the initial mount (the FY-init
  // effect above already runs the first search; a competing search here would
  // clobber the restored filters/sort/page when returning from edit).
  const prevBizIdRef = useRef<number | null | undefined>(undefined)
  useEffect(() => {
    if (!fyInitialized.current) return
    if (prevBizIdRef.current === undefined) { prevBizIdRef.current = bizId; return }
    if (prevBizIdRef.current === bizId) return
    prevBizIdRef.current = bizId
    doSearch(activeFilters, query, dateFrom, dateTo)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizId])

  // ── Derived display ───────────────────────────────────────────────────────

  const sr = searchResults
  const displayTotalSpent  = sr?.totalSpent          ?? 0
  const displayTotalEarned = sr?.totalEarned         ?? 0
  const displayTotal       = sr?.count               ?? 0
  const displayMonthly     = sr?.monthlyTotals       ?? []
  const displayMonthlyInc  = sr?.monthlyIncomeTotals ?? []
  const displayCatTots     = sr?.categoryTotals      ?? []
  const displayTagTots     = sr?.tagTotals           ?? []

  const allItems       = sr?.items ?? []

  const net = displayTotalEarned - displayTotalSpent

  // ── Chart ─────────────────────────────────────────────────────────────────

  const chartResult = buildChartData(
    chartMode, displayMonthly, displayMonthlyInc,
    displayCatTots, displayTagTots,
    displayTotalEarned, displayTotalSpent,
  )
  const { avgInc, avgSpd } = chartResult

  // ── Suggestions ───────────────────────────────────────────────────────────

  const fetchSuggestions = useCallback(async (
    q: string, filters: ActiveFilter[], dfrom: string, dto: string,
  ) => {
    if (q.length < 1) { setShowSug(false); return }
    const fp = buildSearchParams(filters, null, dfrom, dto)
    const result = await getSuggestions(q, {
      merchants: fp.merchants, categories: fp.categories,
      subcategories: fp.subcategories, tags: fp.tags,
      dateFrom: fp.dateFrom, dateTo: fp.dateTo,
      isPersonal: 'false',
      businessId: bizId != null ? String(bizId) : undefined,
    })
    setSuggestions(result)
    setShowSug(true)
    setSugActiveIdx(-1)
  }, [bizId])

  const onQueryChange = (q: string) => {
    setQuery(q)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(q, activeFilters, dateFrom, dateTo)
      doSearch(activeFilters, q, dateFrom, dateTo)
    }, 280)
    if (!q.trim()) {
      setShowSug(false)
      clearTimeout(debounceRef.current)
      doSearch(activeFilters, '', dateFrom, dateTo)
    }
  }

  const applyFilter = useCallback((field: string, value: string, label: string) => {
    clearTimeout(debounceRef.current)
    setActiveFilters(prev => {
      if (prev.some(f => f.field === field && f.value === value)) return prev
      const next = [...prev, { field, value, label }]
      doSearch(next, '', dateFrom, dateTo)
      return next
    })
    setQuery('')
    setShowSug(false)
  }, [doSearch, dateFrom, dateTo])

  const removeFilter = (field: string, value: string) => {
    const next = activeFilters.filter(f => !(f.field === field && f.value === value))
    setActiveFilters(next)
    // If removing a month filter, restore FY date range
    if (field === 'month') {
      const [df, dt] = selectedFY !== null ? getFYDateRange(selectedFY, fy) : [ALL_DATE_FROM, ALL_DATE_TO]
      doSearch(next, query, df, dt)
    } else {
      doSearch(next, query, dateFrom, dateTo)
    }
  }

  // Keyboard nav in suggestions
  const flat: { field: string; value: string; group: string }[] = []
  if (suggestions) {
    suggestions.merchants.forEach(v    => flat.push({ field: 'merchant',    value: v, group: 'Merchant' }))
    suggestions.categories.forEach(v   => flat.push({ field: 'category',    value: v, group: 'Category' }))
    suggestions.subCategories.forEach(v => flat.push({ field: 'subcategory', value: v, group: 'Subcategory' }))
    suggestions.tags.forEach(v         => flat.push({ field: 'tag',          value: v, group: 'Tag' }))
  }

  const onKeyDownSearch = (e: ReactKeyboardEvent) => {
    if (!showSug || flat.length === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setSugActiveIdx(i => Math.min(i + 1, flat.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSugActiveIdx(i => Math.max(i - 1, -1)) }
    if (e.key === 'Escape')    { setShowSug(false) }
    if (e.key === 'Enter') {
      if (sugActiveIdx >= 0 && flat[sugActiveIdx]) {
        const { field, value, group } = flat[sugActiveIdx]
        applyFilter(field, value, group + ': ' + value)
      } else {
        setShowSug(false)
        if (query.trim()) doSearch(activeFilters, query.trim(), dateFrom, dateTo)
      }
    }
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!searchWrapRef.current?.contains(e.target as Node)) setShowSug(false)
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])

  // ── FY change ─────────────────────────────────────────────────────────────

  const changeFY = (fy_: number | null) => {
    setSelectedFY(fy_)
    setActiveFilters([])   // clears any month filter too
    setQuery('')
    setSearchPage(1)
    const [df, dt] = fy_ !== null ? getFYDateRange(fy_, fy) : [ALL_DATE_FROM, ALL_DATE_TO]
    doSearch([], '', df, dt)
  }

  const applyMonthFilter = (year: number, month: number, label: string) => {
    const value   = `${year}-${String(month).padStart(2,'0')}`
    const lastDay = new Date(year, month, 0).getDate()
    const df = `${year}-${String(month).padStart(2,'0')}-01`
    const dt = `${year}-${String(month).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`
    const next = [{ field: 'month', value, label: `Month: ${label}` }]
    setActiveFilters(next)
    setQuery('')
    setSearchPage(1)
    doSearch(next, '', df, dt)
  }

  // Clear the month filter → fall back to the whole selected FY (or all time).
  const clearMonth = () => {
    if (!activeFilters.some(f => f.field === 'month')) return
    const next = activeFilters.filter(f => f.field !== 'month')
    setActiveFilters(next)
    setSearchPage(1)
    const [df, dt] = selectedFY !== null ? getFYDateRange(selectedFY, fy) : [ALL_DATE_FROM, ALL_DATE_TO]
    doSearch(next, query, df, dt)
  }

  // ── Chart click ───────────────────────────────────────────────────────────

  chartClickRef.current = (_event: unknown, elements: { index: number }[]) => {
    if (!elements.length) return
    const label = (chartResult.labels as string[])[elements[0].index]
    if (!label) return
    if (chartMode === 'month') {
      const parts = label.split(' ')
      if (parts.length === 2) {
        const moIdx = MONTH_NAMES.indexOf(parts[0])
        const yr    = parseInt(parts[1], 10)
        if (moIdx >= 0 && yr > 0)
          applyMonthFilter(yr, moIdx + 1, `${parts[0]} '${parts[1].slice(2)}`)
      }
      return
    }
    if (chartMode === 'category') { applyFilter('category', label, 'Category: ' + label); return }
    if (chartMode === 'tag')      { applyFilter('tag',      label, 'Tag: '      + label) }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <h1 className="pb-page-title mb-3">Transactions</h1>

      {/* FY selector */}
      <div className="mb-3">
        <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
          {fyYears.map(y => (
            <button key={y}
              className={`pb-chart-sel-btn${selectedFY === y ? ' active' : ''}`}
              onClick={() => changeFY(y)}>
              {fyLabel(y, fy.followsCalendar)}
            </button>
          ))}
          <button
            className={`pb-chart-sel-btn${selectedFY === null ? ' active' : ''}`}
            onClick={() => changeFY(null)}>
            All
          </button>
        </div>
      </div>

      {/* Month selector — months of the selected fiscal year, up to the current month */}
      {selectedFY !== null && (() => {
        const monthPills  = fyMonthPills(selectedFY, fy)
        const activeMonth = activeFilters.find(f => f.field === 'month')?.value ?? null
        if (monthPills.length === 0) return null
        return (
          <div className="mb-3">
            <div className="pb-chart-selector" style={{ display: 'inline-flex', flexWrap: 'wrap' }}>
              <button className={`pb-chart-sel-btn${activeMonth === null ? ' active' : ''}`} onClick={clearMonth}>All</button>
              {monthPills.map(m => {
                const val = `${m.year}-${String(m.month).padStart(2, '0')}`
                return (
                  <button key={val}
                    className={`pb-chart-sel-btn${activeMonth === val ? ' active' : ''}`}
                    onClick={() => applyMonthFilter(m.year, m.month, m.label)}>
                    {m.label}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* KPI row */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="pb-card pb-card-sm h-100">
            <div className="pb-kpi-label mb-1"><i className="bi bi-arrow-up-circle me-1" />Total Expenses</div>
            <div className="pb-kpi-value">{money(displayTotalSpent)}</div>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="pb-card pb-card-sm h-100">
            <div className="pb-kpi-label mb-1"><i className="bi bi-arrow-down-circle me-1" />Total Revenue</div>
            <div className="pb-kpi-value" style={{ color: '#16a34a' }}>{money(displayTotalEarned)}</div>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="pb-card pb-card-sm h-100">
            <div className="pb-kpi-label mb-1"><i className="bi bi-calculator me-1" />NET</div>
            <div className="pb-kpi-value" style={{ color: net >= 0 ? '#16a34a' : 'var(--pb-danger, #ef4444)' }}>
              {net >= 0 ? '+' : ''}{money(net)}
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="pb-card pb-card-sm h-100">
            <div className="pb-kpi-label mb-1"><i className="bi bi-receipt-cutoff me-1" />Transactions</div>
            <div className="pb-kpi-value">{displayTotal.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="pb-card mb-4">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div className="pb-kpi-label">
              <i className="bi bi-bar-chart-line me-2" style={{ color: 'var(--pb-muted)' }} />Spending
            </div>
            {chartMode === 'month' && avgInc > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.72rem', fontWeight: 600, color: '#059669' }}>
                <svg width="22" height="10" aria-hidden="true"><line x1="0" y1="5" x2="22" y2="5" stroke="rgba(16,185,129,0.85)" strokeWidth="2" strokeDasharray="5,3"/></svg>
                Avg revenue: {money(avgInc)}/mo
              </span>
            )}
            {chartMode === 'month' && avgSpd > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.72rem', fontWeight: 600, color: '#dc2626' }}>
                <svg width="22" height="10" aria-hidden="true"><line x1="0" y1="5" x2="22" y2="5" stroke="rgba(239,68,68,0.8)" strokeWidth="2" strokeDasharray="5,3"/></svg>
                Avg expenses: {money(avgSpd)}/mo
              </span>
            )}
          </div>
          <div className="pb-chart-selector">
            {(['month', 'category', 'tag'] as ChartMode[]).map(m => (
              <button key={m}
                className={`pb-chart-sel-btn${chartMode === m ? ' active' : ''}`}
                onClick={() => setChartMode(m)}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div style={{ position: 'relative', height: 220 }}>
          <Bar
            data={chartResult as never}
            options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                y: {
                  grid: { color: 'rgba(0,0,0,0.06)' },
                  ticks: { callback: (v) => '$' + Number(v).toLocaleString() },
                },
              },
              onClick: (_e, elements) => chartClickRef.current(_e, elements),
            }}
          />
        </div>
      </div>

      {/* Search + filter chips */}
      <div className="mb-4">
        <div ref={searchWrapRef} className="pb-search-wrap">
          <div className="pb-search-box">
            <i className="bi bi-search pb-search-icon" />
            <input
              className="pb-search-input"
              placeholder="Search merchant, category, subcategory or tag…"
              value={query}
              onChange={e => onQueryChange(e.target.value)}
              onKeyDown={onKeyDownSearch}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              className={`pb-search-clear${(query || activeFilters.length > 0) ? ' visible' : ''}`}
              title="Clear search"
              onClick={() => {
                clearTimeout(debounceRef.current)
                setActiveFilters([])
                setQuery('')
                setShowSug(false)
                doSearch([], '', dateFrom, dateTo)
              }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {/* Suggestions dropdown */}
          {showSug && suggestions && flat.length > 0 && (
            <div className="pb-suggestions visible">
              {(['Merchant', 'Category', 'Subcategory', 'Tag'] as const).map(group => {
                const field = group.toLowerCase() === 'subcategory' ? 'subcategory' : group.toLowerCase()
                const items = group === 'Merchant'     ? suggestions.merchants
                            : group === 'Category'    ? suggestions.categories
                            : group === 'Subcategory' ? suggestions.subCategories
                            :                           suggestions.tags
                if (!items.length) return null
                return (
                  <div key={group}>
                    <div className="pb-sug-group-label">{group}</div>
                    {items.map(v => {
                      const fi = flat.findIndex(x => x.field === field && x.value === v)
                      return (
                        <div key={v}
                          className={`pb-sug-item${fi === sugActiveIdx ? ' pb-sug-active' : ''}`}
                          onMouseDown={() => applyFilter(field, v, group + ': ' + v)}>
                          {v}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Active filter chips */}
        {activeFilters.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.65rem' }}>
            {activeFilters.map(f => (
              <span key={f.field + f.value} className="pb-filter-chip">
                <i className="bi bi-funnel-fill" style={{ fontSize: '0.7rem' }} />
                {f.label}
                <button className="pb-filter-chip-clear" onClick={() => removeFilter(f.field, f.value)}>
                  <i className="bi bi-x" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Transactions table */}
      {isSearching && (
        <div style={{ padding: '0.4rem 0', fontSize: '0.8rem', color: 'var(--pb-muted)' }}>
          <span className="spinner-border spinner-border-sm me-2" style={{ width: 12, height: 12 }} />Searching…
        </div>
      )}
      <TransactionsTable
        items={allItems}
        showLink
        stateKey="biz-transactions"
        resetKey={`${selectedFY ?? ''}|${query}|${activeFilters.map(f => f.field + f.value).join(',')}`}
        onRowClick={(id) => {
          snapshotPage<BizTxPageState>('biz-transactions', {
            selectedFY, chartMode, sortField, sortDir, activeFilters, query, searchPage,
          })
          navigate(`/transactions/${id}/edit`)
        }}
      />
    </div>
  )
}
